import { JwtStorage } from './JwtStorage';
import {
  ActiveStatuses, AuthFetch, BackgroundPref, CoreConfig, FrequencyTypes, Permission, PermissionModel, PersonPref,
  User, UserModel, UserRoleModel, UserRole
} from '../../common';

export class CurrentUserService {
  _currentUserPromise?: Promise<User>;
  _permissionsPromise?: Promise<Permission[] | void>;
  _permissionsCache: { [appName: string]: Permission[] } = {};

  constructor( private config: CoreConfig, private authFetch: AuthFetch, private jwtStorage: JwtStorage ) { }

  async find(): Promise<User | undefined> {
    const currentUser = this.jwtStorage.currentUser();

    if (!currentUser) {
      return;
    }

    const roles = await this.findRoles();

    return UserModel.create({ ...currentUser, roles }) as User;
  }

  async findRoles(): Promise<UserRole[]> {
    if (!this.jwtStorage.currentUser()) {
      return [];
    }

    const cachedJson = window.sessionStorage.getItem('roles');

    if (cachedJson && cachedJson !== 'undefined') {
      const cachedRolesJson = JSON.parse(cachedJson) as UserRole[];
      return cachedRolesJson.map(json => UserRoleModel.create(json));
    }

    const params = `statuses=${ActiveStatuses.ACTIVE}&statuses=${ActiveStatuses.SUNSETTING}`;
    const url = `${this.config.prmUrl}/api/current-user?${params}`;

    if (!this._currentUserPromise) {
      this._currentUserPromise = this.authFetch(url) as Promise<User>;
    }

    const personJson = await this._currentUserPromise;
    const rolesJson = personJson && personJson.roles ? personJson.roles : [];

    window.sessionStorage.setItem('roles', JSON.stringify(rolesJson));

    return rolesJson.map(json => UserRoleModel.create(json));
  }

  async findAllPermissions(): Promise<{ [appName: string]: Permission[] }> {
    if (Object.keys(this._permissionsCache).length > 0) {
      return this._permissionsCache;
    }

    if (!this._permissionsPromise) {
      this._permissionsPromise = this.authFetch(`${this.config.prmUrl}/api/current-user/permission`);
    }

    const permissionsJson = await this._permissionsPromise as Permission[];

    this._permissionsCache = permissionsJson
      .map(json => PermissionModel.create(json))
      .reduce(
        (map, permission) => {
          const appName = permission.softwareSystem.name;

          if (!(appName in map)) {
            map[appName] = [];
          }

          map[appName].push(permission);

          return map;
        },
        {} as { [appName: string]: Permission[] }
      );

    const thisAppPermissions = this._permissionsCache[this.config.appName] || [];
    window.sessionStorage.setItem('permissions', JSON.stringify(thisAppPermissions));

    return this._permissionsCache;
  }

  async findPermissions(appName: string = this.config.appName): Promise<Permission[]> {
    if (!this.jwtStorage.currentUser()) {
      return [];
    }

    if (appName === this.config.appName) {
      const cacheJson = window.sessionStorage.getItem('permissions');

      if (cacheJson && cacheJson !== 'undefined') {
        return (JSON.parse(cacheJson) as Permission[])
          .map(json => PermissionModel.create(json));
      }
    }

    const permissionsByApp = await this.findAllPermissions();

    return permissionsByApp[appName] || [];
  }

  async hasPermission(allow: string): Promise<boolean> {
    const permissions = await this.findPermissions();

    return permissions != null && permissions.some(permission => permission.code === allow);
  }

  async impersonate(user: User): Promise<void> {
    const url = `${this.config.prmUrl}/api/current-user/impersonate`;

    const token = await this.authFetch(url, { method: 'POST', body: user }) as string;

    window.sessionStorage.clear();
    this.jwtStorage.set(token);
  }

  // TODO: log this information back to PRM
  async clearImpersonation(): Promise<void> {
    this.jwtStorage.clear();
    window.sessionStorage.clear();
  }

  async findPref(): Promise<PersonPref> {
    if (!this.jwtStorage.currentUser()) {
      return this.buildPref();
    }

    const cacheJson = window.sessionStorage.getItem('user-pref');

    if (cacheJson && cacheJson !== 'undefined') {
      return JSON.parse(cacheJson);
    }

    const url = this.config.prmUrl + '/api/current-user/pref';

    const userPref = await this.authFetch(url) as PersonPref;

    this.cachePref(userPref);

    return userPref;
  }

  cachePref(userPref: PersonPref | string) {
    if (!userPref) {
      return;
    }

    const cacheJson = typeof userPref === 'string' ? userPref : JSON.stringify(userPref);

    if (cacheJson && cacheJson !== 'undefined') {
      window.sessionStorage.setItem('user-pref', cacheJson);
    }
  }

  async savePref(bgPref: BackgroundPref): Promise<PersonPref | undefined> {
    if (!this.jwtStorage.currentUser()) {
      return;
    }

    const url = this.config.prmUrl + '/api/current-user/pref';
    const userPref = this.buildPref(bgPref);

    const savedUserPref = await this.authFetch(url, { method: 'POST', body: userPref }) as PersonPref;

    this.cachePref(savedUserPref);

    return savedUserPref;
  }

  buildPref(params?: BackgroundPref): PersonPref {
    return {
      clazz: 'edu.ucdavis.gs.people.PersonPref',
      backgroundPhoto: params && params.backgroundPhoto,
      backgroundRotation: ( params && params.backgroundRotation ) || FrequencyTypes.QUARTERLY
    };
  }
}



// WEBPACK FOOTER //
// ./src/gs-core/client/auth/CurrentUserService.ts